// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 32-knowledge.md
import * as React from 'react';
import { Plus } from 'lucide-react';

import { EntryRow } from '@/components/knowledge/EntryRow';
import { KnowledgeEntryCreate } from '@/components/knowledge/KnowledgeEntryCreate';
import { ScopeSegments } from '@/components/knowledge/ScopeSegments';
import { ScopeTile } from '@/components/knowledge/ScopeTile';
import {
  canMutateScope,
  formatRelativeDate,
  knowledgeEditorLabel,
} from '@/components/knowledge/util';
import { useScopeResolvers } from '@/components/knowledge/useScopeResolvers';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { KnowledgeEntrySummary, KnowledgeScope } from '@greenlight/contract';

const SCOPE_INTROS: Record<KnowledgeScope, string> = {
  org: 'IT-authored reference every agent reads at session start — design system, security policy, and house conventions.',
  integration:
    'Grouped by the integration each entry describes — open one to read or edit it right here.',
  app: 'Useful context authored by app owners and agents — architecture decisions, runbooks, and hard-won operational notes.',
};

function ColumnHeader(): React.JSX.Element {
  return (
    <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_108px_190px_18px] px-4 md:px-5 py-2 bg-muted/20 border-b border-border font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
      <span>Entry</span>
      <span>Updated</span>
      <span>Last editor</span>
      <span aria-hidden="true" />
    </div>
  );
}

function groupByIntegration(
  entries: KnowledgeEntrySummary[],
): Array<{ integrationId: string; entries: KnowledgeEntrySummary[] }> {
  const map = new Map<string, KnowledgeEntrySummary[]>();
  for (const entry of entries) {
    const key = entry.integrationId ?? 'unknown';
    const list = map.get(key);
    if (list) list.push(entry);
    else map.set(key, [entry]);
  }
  return [...map.entries()].map(([integrationId, items]) => ({ integrationId, entries: items }));
}

export interface EntriesCardProps {
  scope: KnowledgeScope;
  onScopeChange: (s: KnowledgeScope) => void;
  onOpenEntry: (id: string) => void;
  /** Caller's role — gates the "+ New entry" affordance per scope (docs/32 §RBAC). */
  userRole: string | undefined;
}

export function EntriesCard({
  scope,
  onScopeChange,
  onOpenEntry,
  userRole,
}: EntriesCardProps): React.JSX.Element {
  const [createOpen, setCreateOpen] = React.useState(false);
  const orgQ = api.knowledge.listEntries.useQuery(['knowledge-entries', 'org'], {
    query: { scope: 'org' },
  });
  const intQ = api.knowledge.listEntries.useQuery(['knowledge-entries', 'integration'], {
    query: { scope: 'integration' },
  });
  const appQ = api.knowledge.listEntries.useQuery(['knowledge-entries', 'app'], {
    query: { scope: 'app' },
  });
  const propsQ = api.knowledge.listProposals.useQuery(['knowledge-proposals'], {});

  const resolve = useScopeResolvers();

  const orgItems = orgQ.data?.body.items ?? [];
  const intItems = intQ.data?.body.items ?? [];
  const appItems = appQ.data?.body.items ?? [];

  const countFor = (q: typeof orgQ): number | null =>
    q.isLoading || q.isError ? null : (q.data?.body.items.length ?? 0);
  const counts = { org: countFor(orgQ), integration: countFor(intQ), app: countFor(appQ) };

  const byScope = { org: orgQ, integration: intQ, app: appQ } as const;
  const active = byScope[scope];

  const pending = (propsQ.data?.body.items ?? []).filter((p) => p.status === 'pending');
  const pendingFor = (entryId: string): number =>
    pending.filter((p) => p.entryId === entryId).length;

  let body: React.ReactNode;

  if (scope === 'integration') {
    body = intQ.isError ? (
      <div className="px-4 md:px-5 py-4">
        <Alert variant="destructive" data-testid="knowledge-error">
          <AlertTitle>Couldn&apos;t load entries</AlertTitle>
          <AlertDescription>Retry in a moment.</AlertDescription>
        </Alert>
      </div>
    ) : intQ.isLoading ? (
      <div className="px-4 md:px-5 py-4 space-y-2" data-testid="knowledge-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    ) : intItems.length === 0 ? (
      <div data-testid="knowledge-empty-integration" className="px-4 md:px-5 py-6 text-center">
        <p className="text-sm font-medium">No integration entries yet.</p>
      </div>
    ) : (
      <>
        <ColumnHeader />
        <div data-testid="knowledge-entries-list">
          {groupByIntegration(intItems).map(({ integrationId, entries }) => {
            const { tile } = resolve({ scope: 'integration', integrationId, appId: null });
            const name = tile.integrationName ?? tile.integrationSlug ?? 'Integration';
            return (
              <div key={integrationId} className="border-b border-border last:border-0">
                <div className="flex items-center gap-2 px-4 md:px-5 py-2 bg-muted/10">
                  <ScopeTile {...tile} size={24} />
                  <p className="truncate text-[12.5px] font-semibold text-foreground">{name}</p>
                </div>
                <ul className="divide-y divide-border">
                  {entries.map((entry) => (
                    <li key={entry.id}>
                      <EntryRow
                        title={entry.title}
                        topic={entry.topic}
                        version={entry.version}
                        relativeUpdated={formatRelativeDate(entry.updatedAt)}
                        editorLabel={knowledgeEditorLabel(entry.lastEditor, entry.lastEditorKind)}
                        isAgentEditor={entry.lastEditorKind === 'agent'}
                        pendingCount={pendingFor(entry.id)}
                        tile={tile}
                        testId={`knowledge-entry-${entry.topic}`}
                        onOpen={() => onOpenEntry(entry.id)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </>
    );
  } else if (active.isError) {
    body = (
      <div className="px-4 md:px-5 py-4">
        <Alert variant="destructive" data-testid="knowledge-error">
          <AlertTitle>Couldn&apos;t load entries</AlertTitle>
          <AlertDescription>Retry in a moment.</AlertDescription>
        </Alert>
      </div>
    );
  } else if (active.isLoading) {
    body = (
      <div className="px-4 md:px-5 py-4 space-y-2" data-testid="knowledge-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  } else {
    // org or app scope
    const items = scope === 'org' ? orgItems : appItems;
    if (items.length === 0) {
      body = (
        <div data-testid={`knowledge-empty-${scope}`} className="px-4 md:px-5 py-6 text-center">
          <p className="text-sm font-medium">No entries yet.</p>
          <p className="text-muted-foreground text-sm mt-1">
            {scope === 'org'
              ? 'Org entries seed on install, then evolve over time.'
              : 'Add the first entry when there is useful app context worth keeping.'}
          </p>
        </div>
      );
    } else {
      body = (
        <>
          <ColumnHeader />
          <ul className="divide-y divide-border" data-testid="knowledge-entries-list">
            {items.map((entry) => {
              const { tile } = resolve({
                scope: entry.scope,
                integrationId: entry.integrationId,
                appId: entry.appId,
              });
              return (
                <li key={entry.id}>
                  <EntryRow
                    title={entry.title}
                    topic={entry.topic}
                    version={entry.version}
                    relativeUpdated={formatRelativeDate(entry.updatedAt)}
                    editorLabel={knowledgeEditorLabel(entry.lastEditor, entry.lastEditorKind)}
                    isAgentEditor={entry.lastEditorKind === 'agent'}
                    pendingCount={pendingFor(entry.id)}
                    tile={tile}
                    testId={`knowledge-entry-${entry.topic}`}
                    onOpen={() => onOpenEntry(entry.id)}
                  />
                </li>
              );
            })}
          </ul>
        </>
      );
    }
  }

  // New integration entries are still created from the integration's own card
  // on /integrations (unchanged); this page only reads/edits them (docs/28).
  const canCreate = (scope === 'org' || scope === 'app') && canMutateScope(userRole, scope);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-2 border-b border-border">
        <ScopeSegments
          scope={scope}
          counts={counts}
          onChange={(s) => {
            setCreateOpen(false);
            onScopeChange(s);
          }}
        />
        {canCreate && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1 self-center"
            onClick={() => setCreateOpen(true)}
            data-testid="knowledge-new-entry"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New entry
          </Button>
        )}
      </div>
      <section className="rounded-gl-lg border border-border bg-card overflow-hidden">
        <div className="px-4 md:px-5 py-2.5 border-b border-border bg-muted/20">
          <p className="text-[12px] text-muted-foreground">{SCOPE_INTROS[scope]}</p>
        </div>
        {body}
        {(scope === 'org' || scope === 'app') && (
          <KnowledgeEntryCreate
            scope={scope}
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={onOpenEntry}
          />
        )}
      </section>
    </div>
  );
}
